import axios from 'axios';
import type { TFunction } from 'i18next';
import { HTTP_CONFLICT } from '../constants/httpConstants';

// What `LockExceptionHandler` sends when Keycloak cannot resolve the holder's user id to a name.
// It is an English literal, so dropping it into the `{{name}}` slot would put "Another user" in
// the middle of a Danish sentence — it has to be recognized and replaced with a localized phrase.
const UNRESOLVED_HOLDER = 'Another user';

/**
 * The holder name carried by a plan-lock 409, or null when the failure is not a plan lock.
 *
 * Keyed on the body shape, because a 409 alone does not mean "locked": the same status carries a
 * booking overlap, an already-assigned patient, and — the one that has to be excluded by name —
 * the lock *acquire* endpoint's own `LockResponse`, whose `lockedBy` is the holder's raw user
 * UUID. A `status` key is what tells those two bodies apart, and printing a UUID at a user is
 * worse than the generic message it would replace.
 */
const getLockRejection = (error: unknown): { holderName: string | null } | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== HTTP_CONFLICT) return null;
  const body: unknown = error.response.data;
  if (typeof body !== 'object' || body === null || 'status' in body) return null;
  const lockedBy = (body as { lockedBy?: unknown }).lockedBy;
  if (typeof lockedBy !== 'string') return null;
  // Still a lock rejection either way — only the holder is unknown. The handler strips a name it
  // could not resolve to an empty string, and sends the English sentinel when Keycloak had no
  // user to resolve at all; both mean "someone holds this, and we cannot say who".
  const named = lockedBy.length > 0 && lockedBy !== UNRESOLVED_HOLDER;
  return { holderName: named ? lockedBy : null };
};

/**
 * The sentence to show for a plan lock held by `holderName`, or — when the holder could not be
 * resolved to a name — for a plan lock held by someone unnamed.
 *
 * A null holder still says "the plan is locked". Falling back to a generic failure message would
 * drop the one fact the user needs and invite retries that cannot succeed.
 *
 * Separate from {@link getPlanLockedMessage} for the lock *acquire* path, which cannot read the
 * name off the response body (see above) and gets it from the status endpoint instead.
 */
export const buildPlanLockedMessage = (holderName: string | null, t: TFunction): string =>
  holderName ? t('plan-locked-by-error', { name: holderName }) : t('plan-locked-by-unknown-error');

/**
 * The sentence to show for a request rejected because another user holds the scenario lock, or
 * null when the rejection is not a plan lock and the caller should use its own message.
 *
 * Eight endpoints are guarded by `@RequiresScenarioUnlocked` and every one of them answers a lock
 * clash the same way, so the decision lives here once rather than at each call site — a new
 * caller cannot quietly go back to a generic toast, and none of them has to know about the
 * unresolved-holder sentinel.
 */
export const getPlanLockedMessage = (error: unknown, t: TFunction): string | null => {
  const rejection = getLockRejection(error);
  return rejection ? buildPlanLockedMessage(rejection.holderName, t) : null;
};
